import { useEffect, useState } from 'react'

import { Avatar, Box, Checkbox, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { ArrowUpRight } from 'lucide-react'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import DnTable from 'src/components/Core/DnTable'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { UPDATE_TASK } from 'src/graphql/tasks.graphql'
import { TaskStatus, TaskType } from 'src/graphql/types/tasks'
import IconEmail from 'src/icons/IconEmail'
import IconPhone from 'src/icons/IconPhone'
import { buildFullName, formatSnakeValue } from 'src/lib/formatters'
import { PRIORITY_CONFIG } from 'src/lib/taskPriority'
import { STATUS_BADGE_COLOR } from 'src/lib/taskStatus'
import { cn } from 'src/utils'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import { unformatPhoneNumber } from 'src/lib/phone.utils'

// Full, human-readable timestamp used in the Due column hover tooltip.
export const formatTaskDateTime = (date: string) =>
  dayjs(date).format('MMM D, YYYY h:mm A')

// Checkbox lives in its own cell component so each row can hold the optimistic
// completed state and fire the status mutation independently.
const CompleteCheckbox = ({ task, refetch }) => {
  const [isCompleted, setIsCompleted] = useState(
    task.status === TaskStatus.COMPLETED
  )

  // DnTable reuses row slots across pages/filters, so re-sync the optimistic
  // state whenever the underlying task (or its status) changes.
  useEffect(() => {
    setIsCompleted(task.status === TaskStatus.COMPLETED)
  }, [task.taskId, task.status])

  const [updateTask] = useMutation(UPDATE_TASK, {
    onCompleted: () => {
      if (refetch) refetch()
    },
    onError: () => {
      // Roll the optimistic toggle back to the server's value on failure.
      setIsCompleted(task.status === TaskStatus.COMPLETED)
      toast('Error updating task', 'error')
    },
  })

  const handleComplete = () => {
    const next = !isCompleted
    setIsCompleted(next)
    setTimeout(() => {
      updateTask({
        variables: {
          taskId: task.taskId,
          input: {
            status: next ? TaskStatus.COMPLETED : TaskStatus.NOT_STARTED,
          },
        },
      })
    }, 300)
  }

  return (
    <Checkbox
      size="xs"
      style={{ '--checkbox-size': '14px' }}
      checked={isCompleted}
      onClick={(e) => {
        e.stopPropagation()
        handleComplete()
      }}
    />
  )
}

const TasksTable = ({
  tasks,
  loading,
  refetch,
  onRowClick,
  page,
  perPage,
  totalCount,
  onLoadPage,
}) => {
  const { handleOpen: openApplicantDrawer } = useApplicantDrawerContext()
  const phoneDeviceContext = usePhoneDevice()
  const dispatchPhoneAction = usePhoneDeviceDispatch()

  const columns = [
    {
      key: 'title',
      // Indent past the checkbox (14px) + gap (8px) so the header lines up
      // with the task title rather than the checkbox.
      label: <span className="pl-[22px]">Task</span>,
      width: 48,
      render: (task) => (
        <div className="flex w-full items-center gap-2">
          <CompleteCheckbox task={task} refetch={refetch} />
          <Tooltip
            withArrow
            position="top"
            openDelay={300}
            label={
              <div className="flex max-w-xs flex-col gap-0.5 whitespace-normal text-[11px]">
                <span className="font-medium">{task.title}</span>
                {task.description && (
                  <span className="text-doubleNickel-gray-300">
                    {task.description}
                  </span>
                )}
              </div>
            }
          >
            <div className="flex min-w-0 flex-1 items-baseline gap-1.5 text-xs">
              <span className="flex-shrink-0 truncate font-medium text-doubleNickel-gray-900">
                {task.title}
              </span>
              {task.description && (
                <span className="min-w-0 truncate font-medium text-doubleNickel-gray-400">
                  {task.description}
                </span>
              )}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'applicant',
      label: 'Applicant',
      width: 14,
      render: (task) => {
        const applicantId = task.applicant?.applicantId
        if (!applicantId) {
          return (
            <span className="text-xs font-medium text-doubleNickel-gray-400">
              –
            </span>
          )
        }
        const name = buildFullName(task.applicant)
        return (
          <div className="flex w-full items-center gap-1">
            {/* Blue + clickable name opens the applicant sidebar. */}
            <Tooltip label={name} position="top" openDelay={300}>
              <button
                className="block min-w-0 flex-1 truncate text-left text-xs font-medium text-doubleNickel-brand-500 hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  openApplicantDrawer(applicantId)
                }}
              >
                {name}
              </button>
            </Tooltip>
            {/* Arrow jumps to the full applicant details page. */}
            <Tooltip label="Go to applicant details page" position="top">
              <button
                className="flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(
                    routes.applicantDetails({
                      id: applicantId,
                      tab: 'application',
                    })
                  )
                }}
              >
                <ArrowUpRight className="h-3 w-3 text-doubleNickel-gray-400 hover:text-doubleNickel-brand-500" />
              </button>
            </Tooltip>
          </div>
        )
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 10,
      render: (task) => (
        <StyledBadge
          size="xs"
          textTransform="capitalize"
          color={STATUS_BADGE_COLOR[task.status] ?? 'gray'}
        >
          {formatSnakeValue(task.status)}
        </StyledBadge>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      width: 7,
      render: (task) => {
        const priorityConfig = PRIORITY_CONFIG[task.priority]
        if (!priorityConfig) return null
        const { Icon, label, color } = priorityConfig
        return (
          <Tooltip label={label}>
            <Box className={cn('flex w-fit items-center', color)}>
              <Icon className="h-4 w-4" />
            </Box>
          </Tooltip>
        )
      },
    },
    {
      key: 'type',
      label: 'Type',
      width: 5,
      render: (task) => {
        if (task.type === TaskType.CALL) {
          // Show a clickable call icon only when there is an applicant with a phone number.
          if (task.applicant?.phone) {
            return (
              <Tooltip label="Call applicant">
                <button
                  type="button"
                  aria-label="Call applicant"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (phoneDeviceContext?.phoneDevice?.isBusy) return
                    const applicantFullName = buildFullName(task.applicant)
                    dispatchPhoneAction?.({
                      type: PhoneDeviceActionType.CALL,
                      payload: {
                        applicantId: task.applicant.applicantId,
                        phone: unformatPhoneNumber(task.applicant.phone),
                        applicantFullName,
                      },
                    })
                  }}
                  className="group flex h-6 w-6 items-center justify-center rounded hover:bg-doubleNickel-brand-25" // pragma: allowlist secret
                >
                  <IconPhone className="h-3 w-3 fill-none stroke-doubleNickel-gray-500 group-hover:stroke-doubleNickel-brand-600" /> {/* pragma: allowlist secret */}
                </button>
              </Tooltip>
            )
          }
          // No applicant or no phone number — hide the icon entirely.
          return null
        }
        if (task.type === TaskType.EMAIL) {
          return (
            <Tooltip label="Email">
              <Box className="flex w-fit items-center">
                <IconEmail className="h-3 w-3 fill-none stroke-doubleNickel-gray-500" />
              </Box>
            </Tooltip>
          )
        }
        return null
      },
    },
    {
      key: 'assignee',
      label: 'Assignee',
      width: 8,
      render: (task) => {
        const assignee = task.assignees.length > 0 ? task.assignees[0] : null
        if (!assignee) return null
        return (
          <Avatar.Group spacing="0">
            <Tooltip
              key={assignee.employeeId}
              label={`${assignee.firstName} ${assignee.lastName}`}
            >
              <Avatar color="blue" size="sm" radius="sm">
                {assignee.firstName.charAt(0)}
                {assignee.lastName.charAt(0)}
              </Avatar>
            </Tooltip>
            {task.assignees.length > 1 && (
              <Tooltip
                withArrow
                label={
                  <>
                    {task.assignees
                      .filter((a) => a.employeeId !== assignee.employeeId)
                      .map((other) => (
                        <div key={other.employeeId}>
                          {other.firstName} {other.lastName}
                        </div>
                      ))}
                  </>
                }
              >
                <Avatar size="sm" radius="sm">
                  +{task.assignees.length - 1}
                </Avatar>
              </Tooltip>
            )}
          </Avatar.Group>
        )
      },
    },
    {
      key: 'due',
      label: 'Due',
      width: 8,
      render: (task) => {
        if (!task.dueDate) {
          return <span className="text-doubleNickel-gray-300">—</span>
        }
        return (
          <Tooltip
            withArrow
            position="top"
            className="text-[11px]"
            label={
              <div className="flex flex-col gap-0.5">
                <div>{`Due: ${formatTaskDateTime(task.dueDate)}`}</div>
                {task.reminderDate && (
                  <div>{`Reminder: ${formatTaskDateTime(
                    task.reminderDate
                  )}`}</div>
                )}
              </div>
            }
          >
            <span className="text-xs text-doubleNickel-gray-600">
              {dayjs(task.dueDate).format('MM/DD/YYYY')}
            </span>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <DnTable
      columns={columns}
      data={tasks}
      loading={loading}
      loadingRows={8}
      horizontalSpacing={8}
      onRowClick={(_index, rowData) => onRowClick(rowData)}
      emptyStateMessage="No tasks"
      totalCount={totalCount}
      onLoadPage={onLoadPage}
      defaultResultsPerPage={perPage}
      paginationProps={{ initialPage: page }}
    />
  )
}

export default TasksTable
